import { useEffect, useRef, useState } from "react";
import { postJson } from "../api";
import { describeError, getLocale, t } from "../i18n";

/**
 * Sending has to be possible without leaving the call, so this is a plain form
 * that posts to our own server. No mail client, no new tab -- neither works
 * well inside the Discord iframe.
 */

const KINDS = ["request", "bug", "other"] as const;

const MAX_MESSAGE = 1000;

interface Props {
  session: string;
  /** What the sender was looking at, so a report does not have to explain it. */
  context: string;
  /** Sent along so a report can be matched to its lines in the server log. */
  room: string;
  platform: string;
  onClose: () => void;
}

export function FeedbackDialog({ session, context, room, platform, onClose }: Props) {
  const [kind, setKind] = useState<string>("request");
  const [message, setMessage] = useState("");
  const [state, setState] = useState<"editing" | "sending" | "sent">("editing");
  const [error, setError] = useState<string | null>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textarea.current?.focus();
  }, []);

  // Escape closes, but not while a request is in flight.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && state !== "sending") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, state]);

  // Close on its own once the sender has seen the confirmation.
  useEffect(() => {
    if (state !== "sent") return;
    const timer = window.setTimeout(onClose, 1800);
    return () => window.clearTimeout(timer);
  }, [state, onClose]);

  const send = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 4) {
      setError(t("error.tooShort"));
      return;
    }
    setState("sending");
    setError(null);
    try {
      await postJson("/api/feedback", {
        session,
        kind,
        message: trimmed,
        context,
        room,
        platform,
        locale: getLocale(),
      });
      setState("sent");
    } catch (failure) {
      setError(describeError(failure, "feedback.failed"));
      setState("editing");
    }
  };

  return (
    <div className="modal" onClick={(event) => event.target === event.currentTarget && state !== "sending" && onClose()}>
      <div className="modal__panel" role="dialog" aria-modal="true" aria-label={t("feedback.title")}>
        {state === "sent" ? (
          <p className="feedback__done">{t("feedback.sent")}</p>
        ) : (
          <>
            <header className="modal__head">
              <strong>{t("feedback.title")}</strong>
              <button type="button" className="modal__close" onClick={onClose} aria-label={t("pin.close")}>
                ✕
              </button>
            </header>

            <div className="feedback__kinds">
              {KINDS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={kind === option ? "is-active" : ""}
                  onClick={() => setKind(option)}
                  title={t(`feedback.${option}Hint`)}
                >
                  {t(`feedback.${option}`)}
                </button>
              ))}
            </div>

            <textarea
              ref={textarea}
              className="feedback__input"
              value={message}
              maxLength={MAX_MESSAGE}
              placeholder={t("feedback.placeholder")}
              onChange={(event) => setMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) void send();
              }}
            />

            <p className="feedback__note">
              {t("feedback.note")}
            </p>

            {error && <p className="feedback__error">{error}</p>}

            <div className="feedback__actions">
              <span className="feedback__count">
                {message.length}/{MAX_MESSAGE}
              </span>
              <button
                type="button"
                className="feedback__send"
                onClick={() => void send()}
                disabled={state === "sending"}
              >
                {state === "sending" ? t("feedback.sending") : t("feedback.send")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
